<template
  ><v-tour
    name="myTour"
    :steps="steps"
    :options="tourOptions"
    :callbacks="myCallbacks"
  >
    <template slot-scope="tour">
      <transition name="fade">
        <v-step
          v-for="(step, index) of tour.steps"
          v-if="tour.currentStep === index"
          :key="index"
          :step="step"
          :previous-step="tour.previousStep"
          :next-step="tour.nextStep"
          :stop="tour.stop"
          :is-first="tour.isFirst"
          :is-last="tour.isLast"
          :labels="tourOptions.labels"
        >
          <template>
            <div slot="actions">
              <v-btn
                v-if="!tour.isFirst"
                @click.prevent="tour.previousStep"
                depressed
              >
                Forrige
              </v-btn>
              <v-btn
                v-if="!tour.isLast"
                depressed
                @click.prevent="tour.nextStep"
                class="btn btn-primary"
              >
                Neste
              </v-btn>
              <v-btn
                v-if="tour.isLast"
                depressed
                @click.prevent="tour.finish"
                class="btn btn-primary"
              >
                Gå til kartet
              </v-btn>
            </div>
          </template>
          <template v-if="tour.currentStep === 1">
            <div slot="content" style="max-height: 300px; overflow-y: scroll">
              <p>
                Du kan navigere i kartet ved hjelp av mus, tastatur eller
                berøring, alt etter hva du foretrekker. Tabellen under gir en
                oversikt over navigeringsmulighetene.
              </p>
              <table class="tour-table">
                <tr>
                  <th>Interaksjon</th>
                  <th>Oppførsel</th>
                </tr>
                <tr>
                  <td>Klikk og dra</td>
                  <td>Panorerer kartet</td>
                </tr>
                <tr>
                  <td>Dobbeltklikk</td>
                  <td>Zoomer inn mot musepeker</td>
                </tr>
                <tr>
                  <td>Ctrl + dobbeltklikk</td>
                  <td>Zoomer ut fra musepeker</td>
                </tr>
                <tr>
                  <td>Musehjul frem</td>
                  <td>Zoomer inn mot musepeker</td>
                </tr>
                <tr>
                  <td>Musehjul tilbake</td>
                  <td>Zoomer ut fra musepeker</td>
                </tr>
                <tr>
                  <td>Klikk høyre museknapp og dra</td>
                  <td>Rotér kartet når det er i 2d</td>
                </tr>
                <tr>
                  <td>Shift+venstreklikk+dra</td>
                  <td>Zoom til utsnittet av rektangelet</td>
                </tr>
                <tr>
                  <td>N tasten</td>
                  <td>Tilbakestiller rotasjon mot nord</td>
                </tr>
                <tr>
                  <td>A tasten</td>
                  <td>Rotér kartet mot klokken</td>
                </tr>
                <tr>
                  <td>D tasten</td>
                  <td>Rotér kartet med klokken</td>
                </tr>
                <tr>
                  <td>+ tasten</td>
                  <td>Zoom inn mot senter av kartet</td>
                </tr>
                <tr>
                  <td>- tasten</td>
                  <td>Zoom ut fra senter av kartet</td>
                </tr>
                <tr>
                  <td>Dra med en eller flere fingre</td>
                  <td>Panorér</td>
                </tr>
                <tr>
                  <td>Dobbeltapp med én finger</td>
                  <td>Zoom inn</td>
                </tr>
                <tr>
                  <td>Klyp innover/utover med to fingre</td>
                  <td>Zoomer inn eller ut</td>
                </tr>
                <tr>
                  <td>Flytt to fingre med eller mot klokken</td>
                  <td>Rotér kartet i angitt retning</td>
                </tr>
              </table>
            </div>
          </template>
          <template v-if="tour.currentStep === 2">
            <div
              slot="content"
              style="max-height: 300px; overflow-y: scroll; text-align: left;"
            >
              <p>
                Når du klikker på et element i kartet vil det dukke opp et
                popupvindu som viser informasjon om elementet. I tilfeller der
                det er flere overlappende elementer på samme sted kan man
                navigere mellom elementene ved hjelp av pilene som vises i
                popupvinduet.
              </p>
              <img width="280px" src="@/assets/gifs/infowindow.gif" />
              <p>
                Popupvinduet inneholder også en rad med verktøy. Her er en
                oversikt over disse:
              </p>
              <ul>
                <li class="pt-1">
                  &#8226; <b>Høydeprofil:</b> Genererer en høydeprofil (vises
                  kun på linjeelementer).
                </li>
                <li class="pt-2">
                  &#8226; <b>Konsekvensberegning:</b> Analyse for å hente
                  konsekvensfaktorer for et område (vises kun på polygoner).
                </li>
                <li class="pt-2">
                  &#8226; <b>Kopier geometri:</b> Kopierer geometrien til
                  elementet slik at den kan limes inn i tegnelag.
                </li>
                <li class="pt-2">
                  &#8226; <b>Zoom til:</b> Zoomer kartet inn mot elementet.
                </li>
              </ul>
            </div>
          </template>
          <template v-if="tour.currentStep === 12">
            <div
              slot="content"
              style="max-height: 300px; overflow-y: scroll; text-align: left;"
            >
              <p>
                Kartlagverktøyet viser alle kartlag som er lagt til i kartet. Et
                kartlag består vanligvis av flere underkartlag som vist i bildet
                nedenfor.
              </p>

              <img width="280px" src="@/assets/gifs/layers.gif" />
              <p>
                I mange tilfeller er kartlag kun synlige ved visse
                målestokker/zoomnivåer. Hvis et kartlag ikke er synlig gitt
                kartets målestokk vil dette kartlaget gråes ut i listen.
              </p>
              <p>
                Man slår kartlag av eller på ved å klikke på øye-ikonet helt til
                høyre på raden for kartlaget, og justerer synligheten til
                kartlaget enten ved å scrolle opp eller ned mens musepekeren er
                over øye-ikonet, eller ved å bruke pilknappene over- og under
                øye-ikonet.
              </p>
              <p>
                Når man holder musepekeren over et kartlag dukker det opp
                tilleggsvalg for kartlaget. Man kan blant annet se info om et
                kartlag, åpne tabellvisning, og sette filter.
              </p>
              <p>
                Hvis du ønsker å legge til innhold i kartet klikker du på 'Legg
                til innhold' knappen rett over kartlagslisten. Du kan legge til
                NVE kartlag, tegnelag, og laste opp filer. Støttede filtyper er
                shape (zippet), .geojson og .gpx.
              </p>
            </div>
          </template>
          <template v-if="tour.currentStep === 11">
            <div slot="content" style="max-height: 300px; overflow-y: scroll">
              <p>
                Sidemenyen til venstre for kartet inneholder flere nyttige
                verktøy. Man aktiverer et verktøy ved å klikke på et av
                verktøyikonene som vises på verktøylinjen helt til venstre.
              </p>

              <img width="280px" src="@/assets/gifs/sidebar.gif" />
              <p>
                Når løsningen starter opp er kartlagverktøyet automatisk
                aktivert. I neste punkt skal vi se nærmere på kartlagverktøyet.
                Hvis du allerede har prøvd å aktivere andre verktøy er det fint
                om du aktiverer kartlagverktøyet før du klikker 'Neste'.
              </p>
            </div>
          </template>
        </v-step>
      </transition>
    </template>
  </v-tour>
</template>

<script>
import { mapState, mapActions } from "vuex";
export default {
  name: "AppTour",
  computed: {
    ...mapState(["guideIsRunning", "appHelpEmailSubject"])
  },
  methods: {
    ...mapActions(["setGuideIsRunning"]),
    updateGuideIsRunning: function() {
      this.setGuideIsRunning(false);
    }
  },
  data() {
    return {
      myCallbacks: {
        onStop: this.updateGuideIsRunning
      },
      steps: [
        {
          target: '[data-v-step="intro"]',
          header: {
            title: "Informasjon"
          },
          params: {
            placement: "bottom"
          },
          content: `Takk for at du tar deg tid til denne korte guiden. Vennligst vent med å interagere med løsningen inntil guiden er gjennomført. Du kan når som helst avbryte ved å trykke på ESC tasten på tastaturet. <br/><br/> Klikk på 'Neste' for å starte.`
        },
        {
          target: '[data-v-step="navigation"]',
          header: {
            title: "Kartnavigasjon"
          },
          params: {
            placement: "top"
          },
          content: `Du kan navigere i kartet ved hjelp av mus, tastatur eller berøring, alt etter hvilken enhet du bruker. Tabellen under gir en oversikt over navigeringsmulighene.`
        },
        {
          target: '[data-v-step="infowindow"]',
          header: {
            title: "Infovindu"
          },
          params: {
            placement: "top"
          },
          content: `Infovindu`
        },
        {
          target: '[data-v-step="extent"]',
          header: {
            title: "Kartutsnitt"
          },
          params: {
            placement: "top"
          },
          content: `Når du åpner løsningen vil kartet automatisk vise sist brukte kartutsnitt (dvs. kartutsnittet du sist så på forrige gang du brukte løsningen).`
        },
        {
          target: '[data-v-step="1"]',
          header: {
            title: "Temabeskrivelse"
          },
          content: `Ved å klikke på knappen 'Vis temabeskrivelse' vil du få opp en beskrivelse av temaet du ser på. <br/><br/>Det finnes en temabeskrivelse for de fleste av NVE sine tema, men detaljeringsgraden varierer.`
        },
        {
          target: '[data-v-step="2"]',
          header: {
            title: "Bakgrunnskart"
          },
          content: `Du kan endre bakgrunnskart ved hjelp av denne menyen. Du kan velge mellom gråtone, basis, landskap og bilder. Valgt bakgrunnskart huskes til neste gang du åpner løsningen.`
        },
        {
          target: '[data-v-step="3"]',
          header: {
            title: "Kompass"
          },
          params: {
            placement: "left"
          },
          content: `Når du roterer kartet vil kompassnålen rotere samtidig, slik at du alltid vet hvor nord ligger. Du kan tilbakestille rotasjonen til kartet ved å klikke på kompasset.`
        },
        {
          target: '[data-v-step="4"]',
          header: {
            title: "3D visning"
          },
          params: {
            placement: "left"
          },
          content: `Denne knappen brukes for å bytte mellom 2D visning og 3D visning. Merk at 3D visning kan oppleves tregt på noen maskiner.`
        },
        {
          target: '[data-v-step="5"]',
          header: {
            title: "Din posisjon"
          },
          params: {
            placement: "left"
          },
          content: `Denne knappen lar deg gå til din posisjon i kartet. Posisjonen din innhentes ved hjelp av GPS hvis enheten støtter det, eller ved hjelp av nettverksinformasjon. Hvis du bruker en enhet uten GPS vil posisjonen i de fleste tilfeller kun være omtrentlig.`
        },
        {
          target: '[data-v-step="6"]',
          header: {
            title: "Tilbakestill utsnitt"
          },
          params: {
            placement: "left"
          },
          content: `Hvis du ønsker å tilbakestille kartutsnittet til å vise hele landet, klikker du her.`
        },
        {
          target: '[data-v-step="7"]',
          header: {
            title: "Zoom"
          },
          params: {
            placement: "left"
          },
          content: `Disse knappene kan brukes for å zoome inn og ut dersom du ikke har scrollefunksjonalitet tilgjengelig.`
        },
        {
          target: '[data-v-step="menu"]',
          header: {
            title: "Hovedmeny"
          },
          content: "test",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="123"]',
          header: {
            title: "Kartlag"
          },
          content: "test",
          params: {
            placement: "right"
          }
        },
        {
          target: '[data-v-step="search"]',
          header: {
            title: "Søkeverktøy"
          },
          content:
            "Søkeverktøyet lar deg søke etter stedsnavn, adresser, gårds- og bruksnr, og i kartlagene som er lagt til i kartet. Resultatene fra søket oppdateres automatisk mens du skriver inn en søketerm.",
          params: {
            placement: "right"
          }
        },
        {
          target: '[data-v-step="drawing"]',
          header: {
            title: "Tegneverktøy"
          },
          content:
            "Tegneverktøyet lar deg tegne inn punkter, linjer og polygoner i kartet. Du kan gi navn til elementene, velge symbolisering, og ha flere tegnelag. Merk at når tegneverktøyet aktiveres vises det som en egen verktøylinje i kartet. ",
          params: {
            placement: "right"
          }
        },
        {
          target: '[data-v-step="print"]',
          header: {
            title: "Skriv ut"
          },
          content:
            "Utskriftsverktøyet lar deg eksportere et utsnitt av kartet slik at du kan skrive det ut. Du kan velge mellom flere ulike formater og innstillinger.",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="measure"]',
          header: {
            title: "Måleverktøy"
          },
          content:
            "Måleverktøyet gjør det enkelt å måle avstand og areal direkte i kartet. Når 3D visning brukes kan man også måle høydeforskjell.",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="bookmarks"]',
          header: {
            title: "Bokmerker"
          },
          content:
            "Bokmerker gjør det enkelt å gå tilbake til steder du bruker ofte. Merk at bokmerker lagres i nettleseren din.",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="share"]',
          header: {
            title: "Dele kartet"
          },
          content:
            "Hvis du ønsker å dele kartet du ser på med andre kan du gjøre dette ved å klikke på deleikonet. Du vil da få en lenke til kartet som du kan sende til andre.",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="help"]',
          header: {
            title: "Tips og triks"
          },
          content:
            "For å gjøre det lettere å bruke løsningen har vi laget en liste med tips og triks. Du kan se listen ved å klikke på dette spørsmålsikonet.",
          params: {
            placement: "left"
          }
        },
        {
          target: '[data-v-step="final"]',
          header: {
            title: "Hurra!"
          },
          content:
            "Du har nå fått en gjennomgang av den mest essensielle funksjonaliteten i denne løsningen. For mer informasjon anbefaler vi at du ser på tips og triks listen som ble beskrevet i forrige punkt.<br/><br/>Hvis du ønsker å se denne guiden igjen velger du 'Vis guide' i hovedmenyen som vises når du klikker på 'hamburger'-ikonet øverst i verktøyslinjen på venstre side.<br/><br/><p><a href='mailto:gisstotte@nve.no?Subject=" +
            this.appHelpEmailSubject +
            "' target='_top'>Send oss gjerne en epost</a> hvis du har spørsmål eller tilbakemeldinger!</p>",
          params: {
            placement: "left"
          }
        }
      ],
      tourOptions: {
        useKeyboardNavigation: true,
        highlight: true,
        labels: {
          buttonSkip: "Avbryt guide",
          buttonPrevious: "Forrige",
          buttonNext: "Neste",
          buttonStop: "Ferdig"
        }
      }
    };
  }
};
</script>

<style lang="scss">
.tour-table {
  table {
    font-family: arial, sans-serif;
    border-collapse: collapse;
    width: 100%;
  }

  td,
  th {
    border: 1px solid #dddddd;
    text-align: left;
    padding: 8px;
  }

  tr:nth-child(even) {
    background-color: #dddddd;
  }
}
.v-step {
  background: white !important;
  color: #000 !important;
  max-width: 320px !important;
}
.v-step .v-step__arrow {
  border-color: white !important; /* #ffc107, #35495e */
  &--dark {
    border-color: #454d5d;
  }
}
.v-step[x-placement^="top"] {
  margin-bottom: 0.5rem;
}
.v-step[x-placement^="top"] .v-step__arrow {
  border-width: 0.5rem 0.5rem 0 0.5rem;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -0.5rem;
  left: calc(50% - 1rem);
  margin-top: 0;
  margin-bottom: 0;
}
.v-step[x-placement^="bottom"] {
  margin-top: 0.5rem;
}
.v-step[x-placement^="bottom"] .v-step__arrow {
  border-width: 0 0.5rem 0.5rem 0.5rem;
  border-left-color: transparent !important;
  border-right-color: transparent !important ;
  border-top-color: transparent !important;
  top: -0.5rem;
  left: calc(50% - 1rem);
  margin-top: 0;
  margin-bottom: 0;
}
.v-step[x-placement^="right"] {
  margin-left: 0.5rem;
}
.v-step[x-placement^="right"] .v-step__arrow {
  border-width: 0.5rem 0.5rem 0.5rem 0;
  border-left-color: transparent !important;
  border-top-color: transparent !important;
  border-bottom-color: transparent !important;
  left: -0.5rem;
  top: calc(50% - 1rem);
  margin-left: 0;
  margin-right: 0;
}
.v-step[x-placement^="left"] {
  margin-right: 0.5rem;
}
.v-step[x-placement^="left"] .v-step__arrow {
  border-width: 0.5rem 0 0.5rem 0.5rem;
  border-top-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  right: -0.5rem;
  top: calc(50% - 1rem);
  margin-left: 0;
  margin-right: 0;
}

.v-step__header {
  background-color: #c8c8c8 !important;
}
</style>
